/**
 * @file 管理平台下线迁移通栏提示
 * @author sunyingying01
 */

import React from 'react';
import {Alert, Button} from 'antd';
import {CloseOutlined, ExclamationCircleFilled, InfoCircleFilled} from '@ant-design/icons';
import cx from 'classnames';

import {MigrationPhase, goToNewPlatform} from '@/utils/migration';
import {GO_TO_NEW_PLATFORM, NOTICE_CONTENT, getRestrictedBannerText} from '../constants';
import type {MigrationBannerState} from './useMigrationBanner';
import styles from './index.module.less';

/** 轻量通知期文案：说明 + 紧随其后的文字链 */
const noticeMessage = (
    <span>
        {NOTICE_CONTENT}
        <span
            className={styles.link}
            role="button"
            tabIndex={0}
            onClick={goToNewPlatform}
            onKeyDown={e => e.key === 'Enter' && goToNewPlatform()}
        >
            {GO_TO_NEW_PLATFORM}
        </span>
    </span>
);

/** 受限运行期右侧强引导按钮 */
const restrictedAction = (
    <Button
        type="primary"
        size="small"
        className={styles.restrictedBtn}
        onClick={goToNewPlatform}
    >
        {GO_TO_NEW_PLATFORM}
    </Button>
);

const MigrationBanner: React.FC<MigrationBannerState> = ({visible, phase, days, onClose}) => {
    if (!visible) {
        return null;
    }

    // 轻量通知期：文字链引导且可关闭；受限运行期：按钮强引导且不可关闭
    const isNotice = phase === MigrationPhase.NOTICE;
    const message = isNotice ? noticeMessage : getRestrictedBannerText(days);
    const action = isNotice ? null : restrictedAction;
    const closable = isNotice ? {closeIcon: <CloseOutlined />, 'aria-label': '关闭升级提示'} : false;

    return (
        <Alert
            className={cx(styles.banner, isNotice ? styles.noticeBanner : styles.restrictedBanner)}
            type={isNotice ? 'info' : 'warning'}
            icon={isNotice ? <InfoCircleFilled /> : <ExclamationCircleFilled />}
            showIcon
            message={message}
            action={action}
            closable={closable}
            onClose={onClose}
        />
    );
};

export default MigrationBanner;
